// react
import { ReactNode, useContext, useMemo } from 'react';

// types
import { ActionButton, Entity, Frame, Pagination } from '../../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// router
import { useSearchParams } from 'react-router-dom';

// mui
import { Grid } from '@mui/material';

// ui
import { BtnExport } from '../../../ui/BtnExport/BtnExport';
import { EmptyProps } from '../../../ui/Empty/Empty';
import { TableHeaderProps } from '../../../ui/TableHeaders/TableHeader/TableHeader';
import { TableHeaders } from '../../../ui/TableHeaders/TableHeaders';
import { TableFooter } from '../../../ui/TableFooter/TableFooter';

// cards
import { CardEmpty } from '../../../cards/CardEmpty/CardEmpty';
import { CardSupplier } from '../../../cards/CardSupplier/CardSupplier';

// forms
import { FormSearch } from '../../../forms/FormSearch/FormSearch';

// icons
import { AccountBalanceOutlined as IconAccountBalance } from '@mui/icons-material';

// config
import { featureFlags } from '../../../../config/featureFlags';

// pages
import { ContextUserProjectPermissions } from '../../../pages/Project/Project.context';

// props
export interface TableSuppliersProps {
	frame?: Frame;
	isLoading?: boolean;
	pagination: Pagination;
	suppliers: Entity[];
	refetchEntities?: () => void;
}

export const TableSuppliers = ({
	frame,
	isLoading,
	pagination,
	suppliers,
	refetchEntities,
}: TableSuppliersProps) => {
	// state
	const [searchParams, setSearchParams] = useSearchParams();

	// hooks
	const { t } = useTranslation();

	// contexts
	const { userProjectPermissions } = useContext(ContextUserProjectPermissions);

	// params
	const paramQ = searchParams.get('q');

	// vars
	const isFrameSupplier = frame === 'SUPPLIER';

	// Calculate how to distribute the extra space when financial traceability is disabled
	// We have 1 column width unit to distribute across existing columns
	const extraSpace = featureFlags['FINANCIAL_TRACEABILITY'] ? 0 : 0.25;

	// Adjust column widths based on the feature flag
	const nameColWidth = 2 + extraSpace;
	const walletColWidth = 2 + extraSpace;
	const modifiedColWidth = isFrameSupplier ? 3 : 1 + extraSpace;
	const entityColWidth = 2;
	const statusColWidth = 1;
	const contractsColWidth = 1;
	const traceabilityColWidth = 1 + extraSpace;

	// headers
	const headers: TableHeaderProps[] = [
		{
			GridProps: {
				lg: nameColWidth,
			},
			isSortable: true,
			label: t('name'),
			value: 'name',
		},
		{
			GridProps: {
				lg: walletColWidth,
			},
			label: t('wallet'),
			value: 'wallet',
		},
	];

	if (!isFrameSupplier) {
		headers.push({
			GridProps: {
				lg: entityColWidth,
			},
			label: t('entity'),
			value: 'parent',
		});
	}

	headers.push(
		{
			GridProps: {
				lg: statusColWidth,
			},
			label: t('status'),
			value: 'status',
		},
		{
			GridProps: {
				lg: contractsColWidth,
			},
			label: t('contracts'),
			value: 'contracts',
		},
		{
			GridProps: {
				lg: traceabilityColWidth,
			},
			label: t('transactionsEvidenced'),
			tooltip: {
				title: t('tooltip.transactionsEvidenced'),
			},
			value: 'traceabilityTransactions',
		}
	);

	// Add financial traceability column conditionally based on feature flag
	if (featureFlags['FINANCIAL_TRACEABILITY']) {
		headers.push({
			GridProps: {
				lg: 1,
			},
			label: t('financialValueEvidenced'),
			tooltip: {
				title: t('tooltip.financialValueEvidenced'),
			},
			value: 'traceabilityFinancial',
		});
	}

	headers.push({
		GridProps: {
			lg: modifiedColWidth,
		},
		label: t('updated'),
		value: 'modified',
	});

	const empty = useMemo(() => {
		const emptyProps: EmptyProps = {
			actions: [],
			content: { children: t('empty.suppliers.content') },
			IconCircleProps: { icon: (<IconAccountBalance />) as ReactNode },
			title: { children: t('empty.suppliers.title') },
		};

		if (userProjectPermissions?.supplier?.create && emptyProps.actions) {
			emptyProps.actions.push({
				children: t('addSupplier'),
				onClick: () => {
					searchParams.set('dialog', 'addSupplier');
					setSearchParams(searchParams);
				},
				variant: 'contained' as ActionButton['variant'],
			});
		}

		if (paramQ) {
			emptyProps.actions = [];
			emptyProps.content = { children: t('empty.default.content') };
			emptyProps.IconCircleProps = { icon: null };
		}

		return emptyProps;
	}, [paramQ, searchParams, setSearchParams, t, userProjectPermissions]);

	const content = useMemo(() => {
		if (isLoading || !suppliers.length) {
			return <CardEmpty empty={empty} isLoading={isLoading} />;
		}

		return (
			<Grid container spacing={2}>
				{suppliers.map((supplier, i) => {
					return (
						<Grid item key={`supplier-${i}`} xs={12}>
							<CardSupplier frame={frame} supplier={supplier} refetchEntities={refetchEntities} />
						</Grid>
					);
				})}
			</Grid>
		);
	}, [empty, frame, isLoading, refetchEntities, suppliers]);

	return (
		<Grid container spacing={2}>
			<Grid item xs={12}>
				<Grid container spacing={2}>
					<Grid item xs={12} md={true}>
						<FormSearch InputProps={{ placeholder: t('searchByIdOrName') }} />
					</Grid>
					<Grid item xs={12} md="auto">
						<BtnExport resource={'SUPPLIER'} />
					</Grid>
				</Grid>
			</Grid>

			<Grid item xs={12} sx={{ display: { xs: 'none', lg: 'block' } }}>
				<TableHeaders headers={headers} />
			</Grid>
			<Grid item xs={12}>
				{content}
			</Grid>
			<Grid item xs={12}>
				<TableFooter numShowing={suppliers.length} pagination={pagination} />
			</Grid>
		</Grid>
	);
};
